/**
 * Network protocol — single source of truth for both sides.
 *
 * Client → server messages are validated with zod on the server (a malformed
 * message must never crash it). Server → client messages are plain TS types:
 * the server is the trusted authority.
 *
 * Timing model: the server simulates at TICK_RATE (60 Hz) and broadcasts one
 * snapshot every SNAPSHOT_EVERY ticks (30 Hz). The client predicts its own car
 * (inputs applied locally, numbered by `seq`, acknowledged per-player in each
 * snapshot) AND every remote car (dead reckoning with the last input the
 * server applied to it, carried in each snapshot) — the whole displayed world
 * lives on one coherent timeline, so car-car contacts are adjudicated on what
 * the player actually sees. Corrections are smoothed per car.
 */

import { z } from 'zod';
import type { CarConfig, CarDecal, CarFlag, CarStripeStyle } from './car';
import { CAR_DECALS, CAR_FLAGS, CAR_STRIPE_STYLES } from './car';
import type { TrackDesign } from './design';
import { DESIGN_BOUNDS } from './design';
import type { CircuitParams } from './generator';
import { CIRCUIT_PARAM_BOUNDS } from './generator';
import type { AccountInfo } from './stats';
import type { Livery, LeagueSummary, SeasonArchive, SeasonView } from './league';
import type { CarInput, CarState, PhysicsConfig, SurfaceConfigs, SurfaceId } from './physics';
import { PHYSICS_PARAM_BOUNDS, SURFACE_IDS, TICK_RATE } from './physics';

export const PROTOCOL_VERSION = 10;

// ---------------------------------------------------------------------------
// Time-attack ghosts (recorded human lap trajectories)
// ---------------------------------------------------------------------------

/** Which stored lap the client wants to race against. */
export const GHOST_SCOPES = ['record', 'personal'] as const;
export type GhostScope = (typeof GHOST_SCOPES)[number];

/**
 * A replayable lap trajectory: `samples` is a flat [x, y, angle] triplet list,
 * one sample every `sampleSteps` physics steps (60 Hz), first sample at the
 * start line, last at the finish line. The client interpolates between
 * samples against its local lap clock — purely visual, no collision.
 */
export interface GhostData {
  pseudo: string;
  timeMs: number;
  sampleSteps: number;
  samples: number[];
}

export const SNAPSHOT_EVERY = 2; // ticks → 30 Hz
/** Redundant inputs per packet (loss resilience) — server dedups by seq. */
export const INPUT_REDUNDANCY = 5;

// ---------------------------------------------------------------------------
// Races (Phase 4)
// ---------------------------------------------------------------------------

export const RACE_LAPS_BOUNDS = { min: 1, max: 20 } as const;
export const DEFAULT_RACE_LAPS = 5;

// ---------------------------------------------------------------------------
// AI opponents ("adversaires IA") for practice rooms
// ---------------------------------------------------------------------------

/**
 * At most 7 bots per room: the sim is sized for 8 cars and a room must always
 * keep at least one human slot (its creator).
 */
export const MAX_BOTS = 7;

/**
 * Bot strength presets, weakest to strongest. 'elite' drives at the edge of
 * the measured cornering caps; the lower tiers brake earlier, wander more and
 * misjudge corners more often.
 */
export const BOT_SKILL_IDS = ['relax', 'steady', 'fast', 'elite'] as const;
export type BotSkillId = (typeof BOT_SKILL_IDS)[number];

export const BOT_SKILL_LABELS: Record<BotSkillId, string> = {
  relax: 'Tranquille',
  steady: 'Régulier',
  fast: 'Rapide',
  elite: 'Élite',
};
/** Ticks of 3-2-1-GO (inputs locked, cars on the grid). */
export const COUNTDOWN_TICKS = 3 * TICK_RATE;
/** Grace given to stragglers once the winner has finished (SPEC §10). */
export const FINISH_GRACE_TICKS = 60 * TICK_RATE;
/** Results stay on screen this long before the room returns to waiting. */
export const RESULTS_TICKS = 30 * TICK_RATE;

/**
 * waiting  — free driving / warm-up, host can start a race
 * countdown— cars on the grid, inputs locked, 3-2-1-GO
 * racing   — the race is on
 * finished — results are up
 */
export type RaceState = 'waiting' | 'countdown' | 'racing' | 'finished';

export interface Standing {
  id: number;
  /** 1-based live position. */
  pos: number;
  /** Laps completed (0 while on the opening run-up to the line). */
  laps: number;
  finished: boolean;
}

export interface RaceResultRow {
  pos: number;
  playerId: number;
  pseudo: string;
  color: string;
  /** True for an AI opponent — results shown, never persisted. */
  bot?: boolean;
  /** Final car look (design + worn livery merged) — results-screen minicars. */
  design: CarConfig;
  finished: boolean;
  lapsDone: number;
  /** null when the player did not finish. */
  totalTimeMs: number | null;
  bestLapMs: number | null;
  /**
   * Driving time spent on each surface during the race (GO → finish, or race
   * end for a DNF), in ms. Attributed per physics tick to the surface whose
   * config actually drove that tick, so the five values sum to the race time
   * (± one tick around the fractional finish-line crossing).
   */
  surfaceTimesMs: Record<SurfaceId, number>;
  isRaceRecord: boolean;
  isLapRecord: boolean;
  /** League points — always 0 until Phase 6. */
  points: number;
}

// ---------------------------------------------------------------------------
// Client → server (zod-validated)
// ---------------------------------------------------------------------------

const physicsConfigSchema = z.object(
  Object.fromEntries(
    Object.entries(PHYSICS_PARAM_BOUNDS).map(([key, b]) => [
      key,
      z.number().finite().min(b.min).max(b.max),
    ]),
  ) as Record<keyof PhysicsConfig, z.ZodNumber>,
);

const surfaceIdSchema = z.enum([...SURFACE_IDS] as [SurfaceId, ...SurfaceId[]]);

const surfaceConfigsSchema = z.object(
  Object.fromEntries(SURFACE_IDS.map((id) => [id, physicsConfigSchema])) as Record<
    SurfaceId,
    typeof physicsConfigSchema
  >,
);

/** A #rrggbb hex colour. */
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

const liverySchema = z.object({
  name: z.string().trim().min(1).max(30),
  colors: z.array(hexColorSchema).min(1).max(3),
  pattern: z.enum(['solid', 'stripes', 'tricolor', 'halves']),
});

/**
 * A bumper-car design (see car.ts). Validates the HTTP save endpoint's body —
 * exported because the REST layer (server/auth.ts) uses it too. Values are
 * normalized (lower-cased colours, upper-cased number) by sanitizeCarConfig
 * after parsing; this schema only guarantees the shape is legal.
 */
export const carConfigSchema = z.object({
  body: hexColorSchema,
  rubber: hexColorSchema,
  stripe: hexColorSchema,
  glass: hexColorSchema,
  spoilerColor: hexColorSchema,
  decalColor: hexColorSchema,
  stripeStyle: z.enum([...CAR_STRIPE_STYLES] as [CarStripeStyle, ...CarStripeStyle[]]),
  decal: z.enum([...CAR_DECALS] as [CarDecal, ...CarDecal[]]),
  flag: z.enum([...CAR_FLAGS] as [CarFlag, ...CarFlag[]]),
  number: z
    .string()
    .max(2)
    .regex(/^[0-9A-Za-z]*$/),
  spoiler: z.boolean(),
});

const pointsSchemeSchema = z.object({
  positions: z.array(z.number().int().min(0).max(1000)).max(30),
  fastestLapBonus: z.number().int().min(0).max(100),
});

const designAnchorSchema = z.object({
  x: z.number().finite().min(DESIGN_BOUNDS.coord.min).max(DESIGN_BOUNDS.coord.max),
  y: z.number().finite().min(DESIGN_BOUNDS.coord.min).max(DESIGN_BOUNDS.coord.max),
  inX: z.number().finite().min(DESIGN_BOUNDS.handle.min).max(DESIGN_BOUNDS.handle.max),
  inY: z.number().finite().min(DESIGN_BOUNDS.handle.min).max(DESIGN_BOUNDS.handle.max),
  outX: z.number().finite().min(DESIGN_BOUNDS.handle.min).max(DESIGN_BOUNDS.handle.max),
  outY: z.number().finite().min(DESIGN_BOUNDS.handle.min).max(DESIGN_BOUNDS.handle.max),
  width: z.number().finite().min(DESIGN_BOUNDS.width.min).max(DESIGN_BOUNDS.width.max),
  surface: surfaceIdSchema,
  auto: z.boolean().optional(),
});

/** Hand-drawn circuit — geometric quality is re-checked server-side after build. */
const trackDesignSchema = z.object({
  version: z.literal(1),
  anchors: z
    .array(designAnchorSchema)
    .min(DESIGN_BOUNDS.anchors.min)
    .max(DESIGN_BOUNDS.anchors.max),
  startOffset: z
    .number()
    .finite()
    .min(DESIGN_BOUNDS.startOffset.min)
    .max(DESIGN_BOUNDS.startOffset.max),
});

const circuitParamsSchema = z.object({
  corners: z
    .number()
    .int()
    .min(CIRCUIT_PARAM_BOUNDS.corners.min)
    .max(CIRCUIT_PARAM_BOUNDS.corners.max),
  minWidth: z
    .number()
    .int()
    .min(CIRCUIT_PARAM_BOUNDS.minWidth.min)
    .max(CIRCUIT_PARAM_BOUNDS.minWidth.max),
  maxWidth: z
    .number()
    .int()
    .min(CIRCUIT_PARAM_BOUNDS.maxWidth.min)
    .max(CIRCUIT_PARAM_BOUNDS.maxWidth.max),
  approxLength: z
    .number()
    .int()
    .min(CIRCUIT_PARAM_BOUNDS.approxLength.min)
    .max(CIRCUIT_PARAM_BOUNDS.approxLength.max),
  seed: z.number().int().min(0).max(0xffffffff),
});

export const clientMessageSchema = z.discriminatedUnion('type', [
  // Identity comes from the signed session cookie (resolved at the WS upgrade),
  // not from the client — hello only negotiates the protocol version.
  z.object({ type: z.literal('hello'), protocol: z.number().int() }),
  z.object({ type: z.literal('listRooms') }),
  z.object({ type: z.literal('listCircuits') }),
  z.object({
    type: z.literal('createRoom'),
    name: z.string().trim().min(1).max(30),
    /** Total room capacity, bots included (humans = maxPlayers − bots). */
    maxPlayers: z.number().int().min(1).max(8),
    circuitId: z.string().min(1).max(40),
    /** AI opponents seated at creation (practice rooms only — never leagues). */
    bots: z.number().int().min(0).max(MAX_BOTS).optional(),
    botSkill: z.enum([...BOT_SKILL_IDS] as [BotSkillId, ...BotSkillId[]]).optional(),
  }),
  // Exactly one of params (generated) / design (hand-drawn) — enforced server-side.
  z.object({
    type: z.literal('tryCircuit'),
    params: circuitParamsSchema.optional(),
    design: trackDesignSchema.optional(),
  }),
  z.object({
    type: z.literal('saveCircuit'),
    name: z.string().trim().min(1).max(30),
    params: circuitParamsSchema.optional(),
    design: trackDesignSchema.optional(),
  }),
  z.object({ type: z.literal('validateCircuit'), circuitId: z.string().min(1).max(40) }),
  z.object({ type: z.literal('joinRoom'), roomId: z.string().min(1).max(40) }),
  z.object({ type: z.literal('leaveRoom') }),
  z.object({
    type: z.literal('input'),
    inputs: z
      .array(
        z.object({
          seq: z.number().int().nonnegative(),
          throttle: z.number().min(0).max(1),
          brake: z.number().min(0).max(1),
          steer: z.number().min(-1).max(1),
        }),
      )
      .min(1)
      .max(INPUT_REDUNDANCY * 4),
  }),
  z.object({ type: z.literal('reset') }),
  /**
   * Fetch a ghost lap of the CURRENT room's circuit (record = circuit best,
   * personal = own best). Answered with `ghost` (null when nothing recorded).
   */
  z.object({
    type: z.literal('ghostGet'),
    scope: z.enum([...GHOST_SCOPES] as [GhostScope, ...GhostScope[]]),
  }),
  z.object({ type: z.literal('ping'), t: z.number().finite() }),
  /**
   * Télémétrie de fluidité d'un playtest (envoyée ~toutes les 30 s en salon,
   * rate-limitée serveur). Uniquement journalisée côté serveur : elle
   * transforme « ça saccade chez moi » en données lisibles après coup.
   * `gapMaxVisibleMs` = plus long trou entre deux frames onglet VISIBLE (un
   * vrai gel ressenti) ; `gapMaxHiddenMs` = trous imputables à un onglet
   * caché/masqué (alt-tab — PAS un gel du jeu).
   */
  z.object({
    type: z.literal('perfReport'),
    seconds: z.number().finite().min(1).max(600),
    fpsMean: z.number().finite().min(0).max(1000),
    fpsMin: z.number().finite().min(0).max(1000),
    gapMaxVisibleMs: z.number().finite().min(0).max(600_000),
    gapMaxHiddenMs: z.number().finite().min(0).max(86_400_000),
    /** Gels visibles > 100 ms sur la période. */
    freezes100: z.number().int().min(0).max(100_000),
    /** Tas JS utilisé (Chrome), Mo ; -1 si le navigateur ne l'expose pas. */
    heapMb: z.number().finite().min(-1).max(100_000),
    heapMaxMb: z.number().finite().min(-1).max(100_000),
    /** File d'inputs non acquittés max de la période (santé du servo). */
    fileMax: z.number().int().min(0).max(10_000),
    /**
     * Discriminant gel JS / gel graphique (optionnels : clients antérieurs).
     * `timerGapMaxMs` : pire écart d'un setInterval 50 ms — il gèle avec le
     * THREAD JS, mais continue de battre quand seul le pipeline GPU/compositeur
     * cale (rAF arrêté, timers vivants). timerGap ≈ rafGap ⇒ thread bloqué ;
     * timerGap ≪ rafGap ⇒ chaîne graphique.
     */
    timerGapMaxMs: z.number().finite().min(0).max(600_000).optional(),
    /** Long tasks (> 50 ms) du thread principal : nombre et pire durée. */
    longTasks: z.number().int().min(0).max(100_000).optional(),
    longTaskMaxMs: z.number().finite().min(0).max(600_000).optional(),
    /** Ticks simulés par le secours pendant que le rAF était affamé (gel GPU). */
    starvedTicks: z.number().int().min(0).max(100_000).optional(),
    /** Niveau du gouverneur éco automatique (0 = réglages de base). */
    ecoLevel: z.number().int().min(0).max(10).optional(),
    dpr: z.number().finite().min(0).max(10),
    canvasW: z.number().int().min(0).max(20_000),
    canvasH: z.number().int().min(0).max(20_000),
    cores: z.number().int().min(0).max(1024),
    /** navigator.deviceMemory (Go) ; -1 si absent. */
    deviceMemory: z.number().finite().min(-1).max(1024),
    ua: z.string().max(120),
  }),
  /** Host only: number of laps of the next race (SPEC §10, default 5). */
  z.object({
    type: z.literal('setRaceLaps'),
    laps: z.number().int().min(RACE_LAPS_BOUNDS.min).max(RACE_LAPS_BOUNDS.max),
  }),
  /** Host only: grid + 3-2-1-GO. */
  z.object({ type: z.literal('startRace') }),
  /** Host only: back from the results screen to the waiting room. */
  z.object({ type: z.literal('backToLobby') }),
  z.object({ type: z.literal('tuningSet'), surfaces: surfaceConfigsSchema }),
  /**
   * Admin, test-track only: flip the WHOLE room onto one surface (or null to
   * restore the circuit's real surfaces). Lets us feel/tune one surface across
   * the entire lap. Server-side + per-room so prediction and authority agree.
   */
  z.object({
    type: z.literal('tuningSurfaceOverride'),
    surface: surfaceIdSchema.nullable(),
  }),

  // --- Leagues & seasons (Phase 6) ---
  /** Any authenticated user: fetch the league list. */
  z.object({ type: z.literal('leagueList') }),
  /** Fetch one season's live standings + calendar. */
  z.object({ type: z.literal('seasonGet'), seasonId: z.number().int().positive() }),
  /** List a league's past + present seasons (for the archives menu). */
  z.object({ type: z.literal('leagueArchives'), leagueId: z.number().int().positive() }),
  /** Fetch one finished season's archive (final tables + movements). */
  z.object({ type: z.literal('seasonArchiveGet'), seasonId: z.number().int().positive() }),
  /** Equip a livery from the player's unlocked collection (null = plain colour). */
  z.object({ type: z.literal('equipLivery'), livery: liverySchema.nullable() }),

  // Admin only:
  z.object({
    type: z.literal('leagueCreate'),
    name: z.string().trim().min(1).max(40),
    divisions: z.number().int().min(1).max(8),
    pointsScheme: pointsSchemeSchema.optional(),
    rewardLivery: liverySchema.nullable().optional(),
    promoteN: z.number().int().min(0).max(8).optional(),
    relegateN: z.number().int().min(0).max(8).optional(),
  }),
  z.object({
    type: z.literal('seasonCreate'),
    leagueId: z.number().int().positive(),
    circuitIds: z.array(z.string().min(1).max(40)).min(1).max(20),
    assignments: z
      .array(
        z.object({
          playerId: z.number().int().positive(),
          /** 0-based division level (server maps it to the division id). */
          divisionLevel: z.number().int().min(0).max(7),
        }),
      )
      .min(1)
      .max(200),
  }),
  z.object({
    type: z.literal('leagueLaunchRound'),
    seasonId: z.number().int().positive(),
    /** 0-based division level (server maps it to the division id). */
    divisionLevel: z.number().int().min(0).max(7),
    laps: z.number().int().min(RACE_LAPS_BOUNDS.min).max(RACE_LAPS_BOUNDS.max).optional(),
  }),
  z.object({ type: z.literal('seasonClose'), seasonId: z.number().int().positive() }),
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type InputPacketEntry = Extract<ClientMessage, { type: 'input' }>['inputs'][number];

/** Parses a raw ws payload; returns null instead of throwing on any garbage. */
export function parseClientMessage(raw: unknown): ClientMessage | null {
  if (typeof raw !== 'string' || raw.length > 16384) return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const result = clientMessageSchema.safeParse(json);
  return result.success ? result.data : null;
}

// ---------------------------------------------------------------------------
// Server → client (trusted, plain types)
// ---------------------------------------------------------------------------

export interface PlayerPublicInfo {
  id: number;
  pseudo: string;
  color: string;
  connected: boolean;
  /** True for an AI opponent (its pseudo already carries the 🤖 prefix). */
  bot?: boolean;
  /** Equipped livery (multi-colour pattern), or null for a plain body. */
  livery: Livery | null;
  /**
   * The FINAL look of this player's bumper car: their saved design (or the
   * default derived from their colour), with any worn livery already merged by
   * the server (applyLiveryToCarConfig) — clients render it as-is.
   */
  design: CarConfig;
}

export interface RoomSummary {
  id: string;
  name: string;
  circuitName: string;
  /** Occupied slots, AI opponents included. */
  players: number;
  maxPlayers: number;
  /** AI opponents seated in this room (they count in `players`). */
  bots?: number;
  state: RaceState;
  laps: number;
  /** Present when this room is a league round (restricted to a division). */
  league?: { leagueName: string; divisionLevel: number; roundIndex: number } | null;
}

export interface CircuitSummary {
  id: string;
  name: string;
  status: 'draft' | 'validated';
  lengthPx: number;
  createdBy: string;
  recordMs: number | null;
  recordBy: string | null;
}

/** How a client rebuilds the room's track locally (builds are deterministic). */
export interface CircuitDescriptor {
  /** null for an unsaved draft being test-driven. */
  id: string | null;
  name: string;
  /** Built-in hand-made test track (not generator-produced). */
  builtin: boolean;
  /** Present for a generated circuit (builtin false, design null). */
  params: CircuitParams | null;
  /** Present for a hand-drawn circuit (builtin false, params null). */
  design: TrackDesign | null;
  recordMs: number | null;
  recordBy: string | null;
}

export interface CarSnapshot extends CarState {
  id: number;
  surface: SurfaceId;
  /**
   * Input the server applied to this car on the snapshot tick. Remote cars are
   * dead-reckoned with it (held, exactly like the server holds a missing input)
   * until the next snapshot — this is what keeps every displayed car on the
   * local player's timeline, and car-car contacts consistent with the server.
   */
  input: CarInput;
}

/** Everything a client needs to render the race phase, in one object. */
export interface RaceStatus {
  state: RaceState;
  laps: number;
  /** Player id allowed to configure and start races. */
  hostId: number;
  /** Server tick of the GO (countdown), or of the race start (racing). */
  goTick: number;
  /** Ids taking part in the current race (empty while waiting). */
  racerIds: number[];
}

export type ServerMessage =
  | {
      type: 'welcome';
      protocol: number;
      account: AccountInfo;
      surfaces: SurfaceConfigs;
      /** True when this account may edit tuning / validate circuits (admin). */
      canAdmin: boolean;
    }
  | { type: 'rooms'; rooms: RoomSummary[] }
  | {
      type: 'joined';
      roomId: string;
      roomName: string;
      circuit: CircuitDescriptor;
      selfId: number;
      players: PlayerPublicInfo[];
      /** Current car states — initializes prediction and interpolation exactly. */
      cars: CarSnapshot[];
      tick: number;
      /** Persisted best lap of this player on this circuit (null if none). */
      personalBestMs: number | null;
      /** Ongoing lap of the reclaimed car, so the live timer survives reconnects. */
      ownLap: { elapsedSteps: number; nextCp: number } | null;
      race: RaceStatus;
      /** Active whole-circuit surface override (test track tuning), or null. */
      surfaceOverride: SurfaceId | null;
    }
  | { type: 'left' }
  | { type: 'playerJoined'; player: PlayerPublicInfo }
  | { type: 'playerLeft'; playerId: number }
  | { type: 'playerConnection'; playerId: number; connected: boolean }
  | {
      type: 'snapshot';
      tick: number;
      /** Last input seq of THIS recipient applied by the server. */
      ack: number;
      cars: CarSnapshot[];
      /** Live standings + this recipient's gaps — absent outside of a race. */
      live?: {
        standings: Standing[];
        /** ms behind the car ahead / in front of the car behind (null if none). */
        gapAheadMs: number | null;
        gapBehindMs: number | null;
      };
    }
  | { type: 'raceState'; race: RaceStatus }
  | { type: 'raceResults'; laps: number; results: RaceResultRow[] }
  | { type: 'pong'; t: number; tick: number }
  | { type: 'tuning'; surfaces: SurfaceConfigs }
  /** Broadcast to a room when its whole-circuit surface override changes. */
  | { type: 'surfaceOverride'; surface: SurfaceId | null }
  | { type: 'circuits'; circuits: CircuitSummary[] }
  | { type: 'leagues'; leagues: LeagueSummary[] }
  | { type: 'season'; view: SeasonView }
  | { type: 'seasonArchive'; archive: SeasonArchive }
  | {
      type: 'archives';
      leagueId: number;
      seasons: { id: number; number: number; status: 'running' | 'finished' }[];
    }
  /** Acknowledges an equip: the client updates its own account livery. */
  | { type: 'livery'; livery: Livery | null }
  | {
      /** Sent to the lap's driver when they cross the line (server-timed, ms). */
      type: 'lapDone';
      timeMs: number;
      valid: boolean;
      /** Best valid lap this session (null before the first one). */
      sessionBestMs: number | null;
      /** Best valid lap ever for this player on this circuit (persisted). */
      personalBestMs: number | null;
      isRecord: boolean;
    }
  | {
      /** Broadcast to the whole room when the circuit record falls. */
      type: 'record';
      pseudo: string;
      timeMs: number;
    }
  /** Answer to ghostGet — null when no lap is recorded for that scope. */
  | { type: 'ghost'; scope: GhostScope; ghost: GhostData | null }
  | { type: 'error'; code: string; message: string };
